import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ClipboardCheck, MapPin, MessageCircleQuestion, X } from "lucide-react";
import { cachedGet } from "@/lib/publicApi";
import { Skeleton } from "./SaudeLayout";
import { Markdown } from "@/components/Markdown";

const media = (url) => `${process.env.REACT_APP_BACKEND_URL}${url}`;

const Photo = ({ photo, onOpen, index }) => (
  <button
    type="button"
    data-testid={`facility-photo-${photo.id}`}
    onClick={() => onOpen(index)}
    className="group relative block w-full overflow-hidden rounded-2xl bg-sand text-left"
    aria-label={`Abrir foto em tela cheia: ${photo.alt}`}
  >
    <span className="block h-40 w-full animate-pulse bg-sand">
      <img
        src={media(photo.thumb_url)}
        alt={photo.alt}
        loading="lazy"
        decoding="async"
        className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />
    </span>
    {photo.caption ? (
      <span className="block px-4 py-3 text-xs leading-relaxed text-ink-500">{photo.caption}</span>
    ) : null}
  </button>
);

const Lightbox = ({ photos, index, onClose, onMove }) => {
  const startX = useRef(null);
  const photo = photos[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onMove(1);
      if (e.key === "ArrowLeft") onMove(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onMove]);

  return (
    <div
      data-testid="facility-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      className="fixed inset-0 z-[60] flex flex-col bg-ink-900/95 backdrop-blur-sm"
      onTouchStart={(e) => {
        startX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (startX.current === null) return;
        const delta = e.changedTouches[0].clientX - startX.current;
        if (Math.abs(delta) > 48) onMove(delta < 0 ? 1 : -1);
        startX.current = null;
      }}
    >
      <div className="flex items-center justify-between px-5 py-4 text-white">
        <span data-testid="facility-lightbox-indicator" className="text-sm">
          {index + 1} de {photos.length}
        </span>
        <button
          type="button"
          data-testid="facility-lightbox-close"
          onClick={onClose}
          aria-label="Fechar foto"
          className="rounded-full p-2 hover:bg-white/10"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center px-3">
        <img
          key={photo.id}
          src={media(photo.full_url)}
          alt={photo.alt}
          className="max-h-[70vh] w-auto max-w-full rounded-2xl object-contain"
        />
      </div>

      <div className="px-6 pb-10 pt-4 text-white">
        <p className="font-display text-base font-medium">{photo.title}</p>
        {photo.caption ? <p className="mt-1 text-sm text-white/75">{photo.caption}</p> : null}
        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            data-testid="facility-lightbox-prev"
            onClick={() => onMove(-1)}
            aria-label="Foto anterior"
            className="rounded-full border border-white/30 p-3 hover:bg-white/10"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-1.5" aria-hidden="true">
            {photos.map((p, i) => (
              <span key={p.id} className={`h-1.5 w-1.5 rounded-full ${i === index ? "bg-white" : "bg-white/35"}`} />
            ))}
          </div>
          <button
            type="button"
            data-testid="facility-lightbox-next"
            onClick={() => onMove(1)}
            aria-label="Próxima foto"
            className="rounded-full border border-white/30 p-3 hover:bg-white/10"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Facility() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(-1);

  useEffect(() => {
    (async () => {
      try {
        const result = await cachedGet("/facility");
        setData(result.data);
      } catch {
        setData({ title: "Um ambiente pensado para acolher", intro: "", groups: [], contact: {} });
      }
    })();
  }, []);

  const all = data ? data.groups.flatMap((g) => g.photos) : [];
  const move = useCallback(
    (delta) => setOpen((i) => (all.length ? (i + delta + all.length) % all.length : -1)),
    [all.length]
  );

  if (!data) return <Skeleton lines={5} testId="facility-skeleton" />;

  const contact = data.contact || {};

  return (
    <div data-testid="saude-facility-page" className="space-y-12">
      <section>
        <p className="er-label">Estrutura</p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.1] tracking-tight text-ink-900 sm:text-5xl">
          {data.title}
        </h1>
        <Markdown testId="facility-intro" className="mt-5">{data.intro}</Markdown>
      </section>

      {data.groups.every((g) => g.photos.length === 0) ? (
        <p data-testid="facility-empty" className="rounded-2xl bg-sand px-5 py-6 text-sm leading-relaxed text-ink-500">
          As fotos da estrutura serão publicadas em breve. Enquanto isso, você pode solicitar uma avaliação ou tirar
          uma dúvida anonimamente.
        </p>
      ) : null}

      {data.groups.map((group) => (
        <section key={group.slug} data-testid={`facility-group-${group.slug}`}>
          <h2 className="font-display text-lg font-semibold text-ink-900 md:text-lg">{group.title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-500">{group.description}</p>
          {group.photos.length ? (
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {group.photos.map((p) => (
                <Photo key={p.id} photo={p} index={all.findIndex((x) => x.id === p.id)} onOpen={setOpen} />
              ))}
            </div>
          ) : (
            <p className="mt-4 text-sm text-ink-400">Fotos deste grupo ainda não publicadas.</p>
          )}
        </section>
      ))}

      <section data-testid="facility-contact" className="er-card">
        <p className="er-label">Onde estamos</p>
        <p className="mt-3 flex items-start gap-2 text-sm leading-relaxed text-ink-700">
          <MapPin size={16} className="mt-0.5 shrink-0 text-sage-600" aria-hidden="true" />
          {contact.address || "Endereço a confirmar."}
        </p>
        {contact.maps_url ? (
          <a
            href={contact.maps_url}
            target="_blank"
            rel="noreferrer"
            data-testid="facility-maps"
            className="mt-5 flex min-h-[50px] items-center justify-between rounded-full bg-sage-600 px-6 text-sm font-semibold text-white"
          >
            Como chegar <MapPin size={16} />
          </a>
        ) : null}
        <div className="mt-3 space-y-3">
          <Link
            to="/saude/fale-com-a-erimus/avaliacao"
            data-testid="facility-cta-evaluation"
            className="flex min-h-[50px] items-center justify-between rounded-full border border-sage-600/40 px-6 text-sm font-semibold text-sage-700"
          >
            Solicitar avaliação <ClipboardCheck size={16} />
          </Link>
          <Link
            to="/saude/fale-com-a-erimus/pergunta"
            data-testid="facility-cta-question"
            className="flex min-h-[50px] items-center justify-between rounded-full border border-sage-600/40 px-6 text-sm font-semibold text-sage-700"
          >
            Tirar dúvida anonimamente <MessageCircleQuestion size={16} />
          </Link>
        </div>
      </section>

      {open >= 0 && all[open] ? (
        <Lightbox photos={all} index={open} onClose={() => setOpen(-1)} onMove={move} />
      ) : null}
    </div>
  );
}
